import { useState, useEffect, useCallback, useMemo } from 'react';
import api, { trabajadoresApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { validarRutChileno } from '../../lib/rut';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { TableSkeleton } from '../../lib/skeletons';
import { toast } from '../../hooks/use-toast';
import { Plus, Search, Users, Pencil, X, Loader2, Save, UserCheck, UserX, Trash2, AlertTriangle, Eye, Lock } from 'lucide-react';

const emptyForm = () => ({
  numero: '', nombre: '', rut: '', cargo: '', area: '',
  activo: true, telefono: '', correo: '', ficha: '',
  fecha_nacimiento: '', fecha_ingreso: '', afp: '', salud: '',
  direccion: '', fecha_vence_examen: '',
  // Iter 308.g — cuenta principal + grillas beneficiarios/historial
  banco_codigo: '', banco_descripcion: '', cuenta_numero: '',
  beneficiarios_alternos: [],
  historial_contratacion: [],
  // Módulo Vacaciones (Chile)
  zona_extrema: false, anios_previos_reconocidos: '', jefatura_id: '',
  saldo_inicial_vacaciones: '', fecha_corte_saldo: '',
});

const isExamenVencido = (fecha) => {
  if (!fecha) return false;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const f = new Date(fecha);
    return f < today;
  } catch { return false; }
};

const diasHastaExamen = (fecha) => {
  if (!fecha) return null;
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const f = new Date(fecha);
    return Math.floor((f - today) / 86400000);
  } catch { return null; }
};

export default function TrabajadoresPage() {
  const { user } = useAuth();
  const canEditar = useMemo(() => {
    if (!user) return false;
    if (['admin', 'gerencia', 'rrhh', 'rrhh_encargado'].includes(user.rol)) return true;
    return !!user.permisos?.rrhh?.acciones?.trabajadores;
  }, [user]);
  const canVerSensible = useMemo(() => {
    if (!user) return false;
    if (['admin', 'gerencia'].includes(user.rol)) return true;
    if (canEditar) return true;
    return !!user.permisos?.rrhh?.acciones?.trabajadores_datos_sensibles;
  }, [user, canEditar]);
  const [readOnly, setReadOnly] = useState(false);
  const [trabajadores, setTrabajadores] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Iter 308.i (Renzo) — por default mostrar sólo los Activos
  const [filterActivo, setFilterActivo] = useState('true');
  const [filterArea, setFilterArea] = useState('');
  const [areas, setAreas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingNumero, setEditingNumero] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  // Ordenamiento
  const [sortBy, setSortBy] = useState('numero');
  const [sortDir, setSortDir] = useState('asc');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterActivo !== '') params.activo = filterActivo === 'true';
      if (filterArea) params.area = filterArea;
      const [resT, resA, resB] = await Promise.all([
        trabajadoresApi.getAll(params),
        trabajadoresApi.getAreas(),
        api.get('/trabajadores/bancos'),
      ]);
      setTrabajadores(resT.data);
      setAreas(resA.data);
      setBancos(resB.data?.bancos || []);
    } catch {
      toast({ title: 'Error al cargar trabajadores', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filterActivo, filterArea]);

  useEffect(() => { loadData(); }, [loadData]);

  const toggleSort = (campo) => {
    if (sortBy === campo) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(campo); setSortDir('asc'); }
  };

  const sortIcon = (campo) => {
    if (sortBy !== campo) return <span className="text-slate-300 ml-0.5 text-[10px]">↕</span>;
    return <span className="text-blue-600 ml-0.5 font-bold text-[10px]">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const filteredSorted = useMemo(() => {
    let list = trabajadores.filter(t => {
      if (!searchTerm) return true;
      const s = searchTerm.toLowerCase();
      return (
        (t.nombre || '').toLowerCase().includes(s) ||
        (t.rut || '').toLowerCase().includes(s) ||
        (t.cargo || '').toLowerCase().includes(s) ||
        String(t.numero).includes(s)
      );
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      let va = a[sortBy] ?? '';
      let vb = b[sortBy] ?? '';
      if (sortBy === 'numero' || sortBy === 'ficha') {
        va = Number(va) || 0; vb = Number(vb) || 0;
      } else if (sortBy === 'activo') {
        va = a.activo === false ? 0 : 1; vb = b.activo === false ? 0 : 1;
      } else {
        va = String(va).toLowerCase(); vb = String(vb).toLowerCase();
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return list;
  }, [trabajadores, searchTerm, sortBy, sortDir]);

  const openForm = async (trab = null, ro = false) => {
    setReadOnly(ro);
    if (trab) {
      setEditingNumero(trab.numero);
      setFormData({
        numero: trab.numero, nombre: trab.nombre || '', rut: trab.rut || '',
        cargo: trab.cargo || '', area: trab.area || '', activo: trab.activo !== false,
        telefono: trab.telefono || '', correo: trab.correo || '', ficha: trab.ficha || '',
        fecha_nacimiento: trab.fecha_nacimiento || '',
        fecha_ingreso: trab.fecha_ingreso || '',
        afp: trab.afp || '', salud: trab.salud || '',
        direccion: trab.direccion || '',
        fecha_vence_examen: trab.fecha_vence_examen || '',
        // Iter 308.g
        banco_codigo: trab.banco_codigo || '',
        banco_descripcion: trab.banco_descripcion || '',
        cuenta_numero: trab.cuenta_numero || '',
        beneficiarios_alternos: trab.beneficiarios_alternos || [],
        historial_contratacion: trab.historial_contratacion || [],
        zona_extrema: !!trab.zona_extrema,
        anios_previos_reconocidos: trab.anios_previos_reconocidos ?? '',
        jefatura_id: trab.jefatura_id || '',
        saldo_inicial_vacaciones: trab.saldo_inicial_vacaciones ?? '',
        fecha_corte_saldo: trab.fecha_corte_saldo || '',
      });
    } else {
      setEditingNumero(null);
      // Auto-sugerir siguiente correlativo
      let siguiente = '';
      try {
        const res = await api.get('/trabajadores/siguiente-numero');
        siguiente = res.data.siguiente || '';
      } catch (err) { console.warn('[TrabajadoresPage] No se pudo obtener siguiente correlativo:', err); }
      setFormData({ ...emptyForm(), numero: siguiente, ficha: siguiente });
    }
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.nombre) {
      toast({ title: 'Nombre es requerido', variant: 'destructive' });
      return;
    }
    if (formData.rut && formData.rut.trim() && !validarRutChileno(formData.rut)) {
      toast({ title: 'RUT inválido: revisa el dígito verificador (módulo 11)', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        nombre: formData.nombre, cargo: formData.cargo, area: formData.area,
        activo: formData.activo, telefono: formData.telefono,
        correo: formData.correo, rut: formData.rut,
        ficha: formData.ficha ? parseInt(formData.ficha) : null,
        fecha_nacimiento: formData.fecha_nacimiento || null,
        fecha_ingreso: formData.fecha_ingreso || null,
        afp: formData.afp || null, salud: formData.salud || null,
        direccion: formData.direccion || null,
        fecha_vence_examen: formData.fecha_vence_examen || null,
        // Iter 308.g
        banco_codigo: formData.banco_codigo || '',
        banco_descripcion: formData.banco_descripcion || '',
        cuenta_numero: formData.cuenta_numero || '',
        beneficiarios_alternos: formData.beneficiarios_alternos || [],
        historial_contratacion: formData.historial_contratacion || [],
        zona_extrema: !!formData.zona_extrema,
        anios_previos_reconocidos: formData.anios_previos_reconocidos === '' ? 0 : parseInt(formData.anios_previos_reconocidos),
        jefatura_id: formData.jefatura_id || '',
        saldo_inicial_vacaciones: formData.saldo_inicial_vacaciones === '' ? 0 : parseFloat(formData.saldo_inicial_vacaciones),
        fecha_corte_saldo: formData.fecha_corte_saldo || '',
      };
      if (editingNumero !== null) {
        await trabajadoresApi.update(editingNumero, payload);
        toast({ title: 'Trabajador actualizado' });
      } else {
        await trabajadoresApi.create({
          ...payload,
          numero: formData.numero ? parseInt(formData.numero) : null,
        });
        toast({ title: 'Trabajador creado' });
      }
      setShowForm(false);
      loadData();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Error al guardar', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading && trabajadores.length === 0) return <TableSkeleton rows={10} cols={7} />;

  return (
    <div className="space-y-4 page-enter" data-testid="trabajadores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Users className="w-5 h-5" /> Trabajadores</h1>
          <p className="text-xs text-muted-foreground">{filteredSorted.length} de {trabajadores.length}</p>
        </div>
        {canEditar && (
          <Button onClick={() => openForm()} className="rounded-xl gap-1.5" data-testid="btn-nuevo-trabajador">
            <Plus className="h-4 w-4" /> Nuevo Trabajador
          </Button>
        )}
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-3 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input placeholder="Buscar por nombre, RUT, cargo…" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-8 h-9 rounded-xl" data-testid="input-search" />
          </div>
          <select value={filterActivo} onChange={(e) => setFilterActivo(e.target.value)} className="h-9 px-2 rounded-xl border text-sm" data-testid="filter-activo">
            <option value="">Todos</option>
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
          </select>
          <select value={filterArea} onChange={(e) => setFilterArea(e.target.value)} className="h-9 px-2 rounded-xl border text-sm" data-testid="filter-area">
            <option value="">Todas las áreas</option>
            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </CardContent>
      </Card>

      {/* Formulario */}
      {showForm && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{readOnly ? 'Ver' : editingNumero !== null ? 'Editar' : 'Nuevo'} Trabajador</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowForm(false)} className="rounded-xl">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Iter 308.h — RUT como primer dato + link Superintendencia de Pensiones */}
              <div className="space-y-1"><Label className="text-xs">RUT</Label>
                <Input placeholder="12345678-9" value={formData.rut} onChange={(e) => setFormData({ ...formData, rut: e.target.value })} className="rounded-xl" data-testid="input-rut" />
                {formData.rut && formData.rut.trim() && !validarRutChileno(formData.rut) && (
                  <p className="text-xs text-red-500" data-testid="rut-error">DV inválido (módulo 11)</p>
                )}
                <a
                  href="https://www.spensiones.cl/apps/certificados/formConsultaAfiliacion.php"
                  target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                  data-testid="link-consultar-spensiones"
                >
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Consultar en Superintendencia de Pensiones
                </a>
              </div>
              <div className="space-y-1 lg:col-span-2"><Label className="text-xs">Nombre *</Label>
                <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} className="rounded-xl" data-testid="input-nombre" />
              </div>
              <div className="space-y-1"><Label className="text-xs">Numero *</Label>
                <Input type="number" value={formData.numero} onChange={(e) => setFormData({ ...formData, numero: e.target.value })} disabled={editingNumero !== null} className="rounded-xl" data-testid="input-numero" />
                {editingNumero === null && formData.numero && <p className="text-[10px] text-emerald-600">Sugerido (siguiente disponible)</p>}
              </div>
              {/* Iter 308.h — Ficha oculta del UI (queda en DB, auto-sincronizada con numero al crear). */}
              {canVerSensible && (<div className="space-y-1"><Label className="text-xs">Fecha Nacimiento</Label>
                <Input type="date" value={formData.fecha_nacimiento} onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })} className="rounded-xl" data-testid="input-fnac" />
              </div>)}
              <div className="space-y-1"><Label className="text-xs">Fecha Ingreso</Label>
                <Input type="date" value={formData.fecha_ingreso} onChange={(e) => setFormData({ ...formData, fecha_ingreso: e.target.value })} className="rounded-xl" data-testid="input-fing" />
              </div>
              <div className="space-y-1"><Label className="text-xs">Cargo</Label>
                <Input value={formData.cargo} onChange={(e) => setFormData({ ...formData, cargo: e.target.value })} className="rounded-xl" data-testid="input-cargo" />
              </div>
              <div className="space-y-1"><Label className="text-xs">Area</Label>
                <Input value={formData.area} onChange={(e) => setFormData({ ...formData, area: e.target.value })} className="rounded-xl" data-testid="input-area" />
              </div>
              {canVerSensible && (<div className="space-y-1"><Label className="text-xs">AFP</Label>
                <Input placeholder="Habitat / Provida / etc." value={formData.afp} onChange={(e) => setFormData({ ...formData, afp: e.target.value })} className="rounded-xl" data-testid="input-afp" />
              </div>)}
              {canVerSensible && (<div className="space-y-1"><Label className="text-xs">Salud</Label>
                <Input placeholder="Fonasa / Isapre X" value={formData.salud} onChange={(e) => setFormData({ ...formData, salud: e.target.value })} className="rounded-xl" data-testid="input-salud" />
              </div>)}
              {canVerSensible && (<div className="space-y-1 lg:col-span-2"><Label className="text-xs">Dirección</Label>
                <Input value={formData.direccion} onChange={(e) => setFormData({ ...formData, direccion: e.target.value })} className="rounded-xl" data-testid="input-direccion" />
              </div>)}
              {canVerSensible && (<div className="space-y-1"><Label className="text-xs">Teléfono</Label>
                <Input value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} className="rounded-xl" />
              </div>)}
              {canVerSensible && (<div className="space-y-1"><Label className="text-xs">Correo</Label>
                <Input type="email" value={formData.correo} onChange={(e) => setFormData({ ...formData, correo: e.target.value })} className="rounded-xl" />
              </div>)}
              <div className="space-y-1"><Label className="text-xs">Vence Examen Ocupacional</Label>
                <Input type="date" value={formData.fecha_vence_examen} onChange={(e) => setFormData({ ...formData, fecha_vence_examen: e.target.value })} className="rounded-xl" data-testid="input-examen" />
                {formData.fecha_vence_examen && isExamenVencido(formData.fecha_vence_examen) && (
                  <p className="text-[10px] text-red-600 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> Vencido</p>
                )}
              </div>
            </div>
            {/* Iter 308.g — Cuenta bancaria principal + Grillas Beneficiarios/Historial */}
            <div className="mt-5 space-y-4">
              {!canVerSensible && (
                <div className="border rounded-lg p-3 bg-amber-50 text-amber-800 text-xs flex items-center gap-2" data-testid="sensible-restringido-notice">
                  <Lock className="w-3.5 h-3.5" /> Datos sensibles ocultos (contacto, previsión, dirección y datos bancarios). Requiere el permiso &quot;Ver datos sensibles&quot;.
                </div>
              )}
              {canVerSensible && (<>
              {/* Cuenta principal */}
              <div className="border rounded-lg p-3 bg-slate-50">
                <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Cuenta bancaria de sueldo</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Banco</Label>
                    <select
                      value={formData.banco_codigo || ''}
                      onChange={(e) => {
                        const b = bancos.find(x => String(x.codigo_cmf) === e.target.value);
                        setFormData({
                          ...formData,
                          banco_codigo: e.target.value,
                          banco_descripcion: b?.descripcion || '',
                        });
                      }}
                      className="w-full h-9 px-2 text-sm rounded-md border border-input bg-background"
                      data-testid="select-banco-principal"
                    >
                      <option value="">— Sin banco —</option>
                      {bancos.map(b => (
                        <option key={b.codigo_cmf} value={b.codigo_cmf}>{b.codigo_cmf} · {b.descripcion}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">N° Cuenta</Label>
                    <Input
                      value={formData.cuenta_numero}
                      onChange={(e) => setFormData({ ...formData, cuenta_numero: e.target.value })}
                      className="rounded-xl"
                      data-testid="input-cuenta-numero"
                    />
                  </div>
                </div>
              </div>

              {/* Beneficiarios alternos */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold uppercase text-slate-600">Beneficiarios alternos (pagos a otro RUT)</div>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => setFormData({
                      ...formData,
                      beneficiarios_alternos: [
                        ...(formData.beneficiarios_alternos || []),
                        { rut_beneficiario: '', nombre: '', banco_codigo: '', banco_descripcion: '', cuenta_numero: '', activo: true },
                      ],
                    })}
                    className="gap-1"
                    data-testid="btn-add-beneficiario"
                  ><Plus className="w-3 h-3" /> Agregar</Button>
                </div>
                {(formData.beneficiarios_alternos || []).length === 0 && (
                  <p className="text-xs text-muted-foreground italic">Sin beneficiarios alternos.</p>
                )}
                {(formData.beneficiarios_alternos || []).map((b, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end mb-2 pb-2 border-b last:border-b-0" data-testid={`beneficiario-row-${idx}`}>
                    <div className="md:col-span-3">
                      <Label className="text-[10px]">RUT Beneficiario</Label>
                      <Input value={b.rut_beneficiario} placeholder="12345678-9"
                        onChange={(e) => {
                          const arr = [...formData.beneficiarios_alternos];
                          arr[idx] = { ...arr[idx], rut_beneficiario: e.target.value };
                          setFormData({ ...formData, beneficiarios_alternos: arr });
                        }}
                        className="text-xs h-8" data-testid={`benef-rut-${idx}`} />
                      {b.rut_beneficiario && !validarRutChileno(b.rut_beneficiario) && (
                        <p className="text-[10px] text-red-500">RUT inválido</p>
                      )}
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-[10px]">Nombre</Label>
                      <Input value={b.nombre}
                        onChange={(e) => {
                          const arr = [...formData.beneficiarios_alternos];
                          arr[idx] = { ...arr[idx], nombre: e.target.value };
                          setFormData({ ...formData, beneficiarios_alternos: arr });
                        }}
                        className="text-xs h-8" />
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-[10px]">Banco</Label>
                      <select value={b.banco_codigo || ''}
                        onChange={(e) => {
                          const arr = [...formData.beneficiarios_alternos];
                          const bnc = bancos.find(x => String(x.codigo_cmf) === e.target.value);
                          arr[idx] = { ...arr[idx], banco_codigo: e.target.value, banco_descripcion: bnc?.descripcion || '' };
                          setFormData({ ...formData, beneficiarios_alternos: arr });
                        }}
                        className="w-full h-8 px-1.5 text-xs rounded-md border border-input bg-background">
                        <option value="">—</option>
                        {bancos.map(bn => <option key={bn.codigo_cmf} value={bn.codigo_cmf}>{bn.codigo_cmf} · {bn.descripcion}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px]">Cuenta</Label>
                      <Input value={b.cuenta_numero}
                        onChange={(e) => {
                          const arr = [...formData.beneficiarios_alternos];
                          arr[idx] = { ...arr[idx], cuenta_numero: e.target.value };
                          setFormData({ ...formData, beneficiarios_alternos: arr });
                        }}
                        className="text-xs h-8" />
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      <Button type="button" size="icon" variant="ghost"
                        onClick={() => {
                          const arr = [...formData.beneficiarios_alternos];
                          arr.splice(idx, 1);
                          setFormData({ ...formData, beneficiarios_alternos: arr });
                        }}
                        className="h-8 w-8 text-red-600"
                        data-testid={`btn-del-benef-${idx}`}
                      ><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
              </>)}

              {/* Historial de contratación */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold uppercase text-slate-600">Historial de contratación (rotación)</div>
                  <Button
                    type="button" size="sm" variant="outline"
                    onClick={() => setFormData({
                      ...formData,
                      historial_contratacion: [
                        ...(formData.historial_contratacion || []),
                        { fecha_ingreso: '', fecha_finiquito: '', ot: '', estado: 'Activo', motivo: '' },
                      ],
                    })}
                    className="gap-1"
                    data-testid="btn-add-historial"
                  ><Plus className="w-3 h-3" /> Agregar</Button>
                </div>
                <p className="text-[11px] text-muted-foreground mb-1.5">
                  El estado global &quot;Activo/Inactivo&quot; se sincroniza con el último item del historial (si tiene fecha de finiquito o estado Inactivo → trabajador Inactivo).
                </p>
                {(formData.historial_contratacion || []).length === 0 && (
                  <p className="text-xs text-muted-foreground italic">Sin registros de contratación.</p>
                )}
                {(formData.historial_contratacion || []).map((h, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end mb-2 pb-2 border-b last:border-b-0" data-testid={`historial-row-${idx}`}>
                    <div className="md:col-span-2">
                      <Label className="text-[10px]">Fecha Ingreso</Label>
                      <Input type="date" value={h.fecha_ingreso || ''}
                        onChange={(e) => {
                          const arr = [...formData.historial_contratacion];
                          arr[idx] = { ...arr[idx], fecha_ingreso: e.target.value };
                          setFormData({ ...formData, historial_contratacion: arr });
                        }}
                        className="text-xs h-8" data-testid={`hist-ing-${idx}`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px]">Fecha Finiquito</Label>
                      <Input type="date" value={h.fecha_finiquito || ''}
                        onChange={(e) => {
                          const arr = [...formData.historial_contratacion];
                          arr[idx] = { ...arr[idx], fecha_finiquito: e.target.value };
                          // Si tiene finiquito, forzar estado Inactivo
                          if (e.target.value) arr[idx].estado = 'Inactivo';
                          setFormData({ ...formData, historial_contratacion: arr });
                        }}
                        className="text-xs h-8" data-testid={`hist-fin-${idx}`} />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px]">OT</Label>
                      <Input value={h.ot || ''}
                        onChange={(e) => {
                          const arr = [...formData.historial_contratacion];
                          arr[idx] = { ...arr[idx], ot: e.target.value };
                          setFormData({ ...formData, historial_contratacion: arr });
                        }}
                        className="text-xs h-8" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px]">Estado</Label>
                      <select value={h.estado || 'Activo'}
                        onChange={(e) => {
                          const arr = [...formData.historial_contratacion];
                          arr[idx] = { ...arr[idx], estado: e.target.value };
                          setFormData({ ...formData, historial_contratacion: arr });
                        }}
                        className="w-full h-8 px-1.5 text-xs rounded-md border border-input bg-background"
                        data-testid={`hist-estado-${idx}`}>
                        <option value="Activo">Activo</option>
                        <option value="Inactivo">Inactivo</option>
                      </select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-[10px]">Motivo</Label>
                      <Input value={h.motivo || ''}
                        onChange={(e) => {
                          const arr = [...formData.historial_contratacion];
                          arr[idx] = { ...arr[idx], motivo: e.target.value };
                          setFormData({ ...formData, historial_contratacion: arr });
                        }}
                        className="text-xs h-8" />
                    </div>
                    <div className="md:col-span-1 flex justify-end">
                      <Button type="button" size="icon" variant="ghost"
                        onClick={() => {
                          const arr = [...formData.historial_contratacion];
                          arr.splice(idx, 1);
                          setFormData({ ...formData, historial_contratacion: arr });
                        }}
                        className="h-8 w-8 text-red-600"
                        data-testid={`btn-del-hist-${idx}`}
                      ><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Vacaciones / Feriado Legal (Chile) */}
              <div className="border rounded-lg p-3 bg-blue-50/40" data-testid="seccion-vacaciones">
                <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Vacaciones / Feriado Legal (Chile)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Jefatura (aprueba N1)</Label>
                    <select
                      value={formData.jefatura_id || ''}
                      onChange={(e) => setFormData({ ...formData, jefatura_id: e.target.value })}
                      className="w-full h-9 px-2 text-sm rounded-md border border-input bg-background"
                      data-testid="select-jefatura"
                    >
                      <option value="">— Sin jefatura (lo resuelve RRHH) —</option>
                      {trabajadores.filter((t) => t.numero !== editingNumero).map((t) => (
                        <option key={t.numero} value={String(t.numero)}>{t.numero} · {t.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Años previos reconocidos (máx. 10)</Label>
                    <Input type="number" min="0" max="10" value={formData.anios_previos_reconocidos}
                      onChange={(e) => setFormData({ ...formData, anios_previos_reconocidos: e.target.value })}
                      className="rounded-xl" data-testid="input-anios-previos" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm cursor-pointer h-9">
                      <input type="checkbox" checked={!!formData.zona_extrema}
                        onChange={(e) => setFormData({ ...formData, zona_extrema: e.target.checked })}
                        className="rounded" data-testid="check-zona-extrema" />
                      Zona Extrema (20 días/año)
                    </label>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Saldo inicial (días)</Label>
                    <Input type="number" step="0.01" value={formData.saldo_inicial_vacaciones}
                      onChange={(e) => setFormData({ ...formData, saldo_inicial_vacaciones: e.target.value })}
                      className="rounded-xl" data-testid="input-saldo-inicial" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Fecha de corte del saldo</Label>
                    <Input type="date" value={formData.fecha_corte_saldo}
                      onChange={(e) => setFormData({ ...formData, fecha_corte_saldo: e.target.value })}
                      className="rounded-xl" data-testid="input-fecha-corte" />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">El devengo se calcula de forma continua desde la fecha de ingreso (o desde la fecha de corte + saldo inicial, si se indica).</p>
              </div>
            </div>
            </fieldset>
            <div className="flex items-center gap-4 mt-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={formData.activo} disabled={readOnly} onChange={(e) => setFormData({ ...formData, activo: e.target.checked })} className="rounded" data-testid="check-activo" />
                Activo
              </label>
              <div className="flex-1" />
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-xl">{readOnly ? 'Cerrar' : 'Cancelar'}</Button>
              {!readOnly && (
                <Button onClick={handleSave} disabled={saving} className="rounded-xl gap-1.5" data-testid="btn-guardar-trabajador">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Guardar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16 cursor-pointer select-none hover:bg-slate-50" onClick={() => toggleSort('numero')} data-testid="sort-numero">N{sortIcon('numero')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:bg-slate-50" onClick={() => toggleSort('nombre')} data-testid="sort-nombre">Nombre{sortIcon('nombre')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:bg-slate-50" onClick={() => toggleSort('rut')} data-testid="sort-rut">RUT{sortIcon('rut')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:bg-slate-50" onClick={() => toggleSort('cargo')} data-testid="sort-cargo">Cargo{sortIcon('cargo')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:bg-slate-50" onClick={() => toggleSort('area')} data-testid="sort-area">Área{sortIcon('area')}</TableHead>
                  <TableHead className="cursor-pointer select-none hover:bg-slate-50" onClick={() => toggleSort('fecha_vence_examen')} data-testid="sort-examen">Vence Examen{sortIcon('fecha_vence_examen')}</TableHead>
                  <TableHead className="text-center cursor-pointer select-none hover:bg-slate-50" onClick={() => toggleSort('activo')} data-testid="sort-activo">Estado{sortIcon('activo')}</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSorted.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No se encontraron trabajadores</TableCell>
                  </TableRow>
                ) : (
                  filteredSorted.map((t) => {
                    const vencido = isExamenVencido(t.fecha_vence_examen);
                    const dias = diasHastaExamen(t.fecha_vence_examen);
                    const proximo = !vencido && dias !== null && dias <= 30;
                    return (
                      <TableRow key={t.numero} className={`hover:bg-muted/50 transition-colors ${vencido ? 'bg-red-50/60 dark:bg-red-950/20' : ''}`} data-testid={`row-trabajador-${t.numero}`}>
                        <TableCell className={`font-mono text-sm tabular-nums ${vencido ? 'text-red-700 font-bold' : ''}`}>{t.numero}</TableCell>
                        <TableCell className={`font-medium ${vencido ? 'text-red-700' : ''}`}>
                          {vencido && <AlertTriangle className="inline w-3.5 h-3.5 mr-1 text-red-600" />}
                          {t.nombre}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{t.rut || '-'}</TableCell>
                        <TableCell className="text-sm">{t.cargo || '-'}</TableCell>
                        <TableCell className="text-sm">{t.area || '-'}</TableCell>
                        <TableCell className={`text-xs tabular-nums ${vencido ? 'text-red-700 font-bold' : proximo ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}`}>
                          {t.fecha_vence_examen || '-'}
                          {vencido && <span className="ml-1">({Math.abs(dias)}d vencido)</span>}
                          {proximo && <span className="ml-1">({dias}d)</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          {t.activo !== false ? (
                            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><UserCheck className="h-3 w-3" /> Activo</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400"><UserX className="h-3 w-3" /> Inactivo</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openForm(t, true)} className="rounded-xl h-8 w-8" title="Ver ficha" data-testid={`btn-ver-${t.numero}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            {canEditar && (<>
                            <Button variant="ghost" size="icon" onClick={() => openForm(t, false)} className="rounded-xl h-8 w-8" title="Editar" data-testid={`btn-edit-${t.numero}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={async () => {
                                if (!window.confirm(`¿Eliminar al trabajador ${t.nombre} (N° ${t.numero})?\n\nSolo se puede eliminar si no tiene partes diarios, HH ni asistencias.`)) return;
                                try {
                                  await api.delete(`/trabajadores/${t.numero}`);
                                  toast({ title: `Trabajador ${t.nombre} eliminado` });
                                  loadData();
                                } catch (err) {
                                  toast({ title: 'No se pudo eliminar', description: err?.response?.data?.detail || 'Error', variant: 'destructive' });
                                }
                              }}
                              className="rounded-xl h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                              title="Eliminar trabajador" data-testid={`btn-delete-${t.numero}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                            </>)}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
